/**
 * iter150AL — Measurement extraction WebSocket subscription helper.
 *
 * One page can render 6+ `MeasurementCapture` cards (3-phase voltage =
 * 6 slots). Opening 6 separate WebSocket connections would be wasteful
 * on mobile, so this module multiplexes all subscribers for the same
 * `(parentKind, parentId)` over a single shared WS.
 *
 * Subscribers register a callback; we refcount so the socket closes
 * automatically once the last `MeasurementCapture` for that parent
 * unmounts.
 *
 * The poll loop in `MeasurementCapture` is the always-on fallback —
 * this hook is a pure latency optimisation that fires `1-1.5s sooner`
 * on networks that allow WS upgrades. If the upgrade is blocked
 * (corporate proxies, some service workers, captive portals), the
 * polling already resolves the reading and the UX degrades gracefully.
 */
import { useEffect, useRef } from "react";

const _API = process.env.REACT_APP_BACKEND_URL || "";

/** Map<"parent_kind:parent_id", SharedSocket> */
const _shared = new Map();

class SharedSocket {
  constructor(parentKind, parentId, email) {
    this.parentKind = parentKind;
    this.parentId = parentId;
    this.email = email;
    this.subs = new Set();              // Set<(payload) => void>
    this.ws = null;
    this.refcount = 0;
    this.pingTimer = null;
    this.closedByUs = false;
    this._open();
  }

  _open() {
    try {
      const base = _API.replace(/^http/, "ws");
      const url = new URL(`${base}/api/measurements/ws`);
      url.searchParams.set("parent_kind", this.parentKind);
      url.searchParams.set("parent_id", this.parentId);
      url.searchParams.set("email", this.email || "");
      this.ws = new WebSocket(url.toString());
      this.ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data || "{}");
          if (msg.type === "subscribed" || msg.type === "pong") return;
          if (msg.type === "measurement_done" || msg.type === "measurement_failed") {
            for (const cb of this.subs) {
              try { cb(msg); } catch { /* sub callback error — never break the fan-out */ }
            }
          }
        } catch { /* malformed payload — ignore */ }
      };
      this.ws.onerror = () => { /* errors surface as onclose */ };
      this.ws.onclose = () => {
        if (this.pingTimer) clearInterval(this.pingTimer);
        this.pingTimer = null;
        // No reconnect — polling will catch any update we miss while
        // we're down. The next mount of a MeasurementCapture for this
        // parent will open a fresh socket if anyone still cares.
      };
      // Keepalive every 25s; mobile carriers love to kill idle sockets.
      this.pingTimer = setInterval(() => {
        if (this.ws?.readyState === WebSocket.OPEN) {
          try { this.ws.send(JSON.stringify({ type: "ping" })); } catch { /* socket closing */ }
        }
      }, 25000);
    } catch {
      // WebSocket constructor itself threw (very old browser, restricted env).
      // Polling fallback in MeasurementCapture handles this case silently.
    }
  }

  subscribe(cb) {
    this.subs.add(cb);
    this.refcount += 1;
    return () => {
      this.subs.delete(cb);
      this.refcount -= 1;
      if (this.refcount <= 0) {
        this.closedByUs = true;
        try { this.ws?.close(); } catch { /* already closed */ }
        if (this.pingTimer) clearInterval(this.pingTimer);
        _shared.delete(`${this.parentKind}:${this.parentId}`);
      }
    };
  }
}

/**
 * React hook — subscribes to extraction events for one parent record.
 *
 * @param {string|null} parentKind  e.g. "commissioning" / "warranty_claim"
 * @param {string|null} parentId    the parent doc id
 * @param {string|null} email       used for the WS auth query param
 * @param {(msg: {type, record}) => void} onMessage
 *
 * Pass `null` for `parentKind`/`parentId` to disable. The hook
 * silently no-ops if `WebSocket` isn't available in the environment.
 */
export function useMeasurementWs(parentKind, parentId, email, onMessage) {
  const cbRef = useRef(onMessage);
  useEffect(() => { cbRef.current = onMessage; }, [onMessage]);

  useEffect(() => {
    if (!parentKind || !parentId) return undefined;
    if (typeof WebSocket === "undefined") return undefined;
    const key = `${parentKind}:${parentId}`;
    let shared = _shared.get(key);
    if (!shared) {
      shared = new SharedSocket(parentKind, parentId, email);
      _shared.set(key, shared);
    }
    const unsub = shared.subscribe((msg) => cbRef.current?.(msg));
    return () => { unsub(); };
  }, [parentKind, parentId, email]);
}
